import { Undo2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { WRITE_ROLES, useAuth } from "../lib/auth";
import { fmtDate, fmtIDR } from "../lib/format";
import { Button, Field, Modal, Textarea } from "./ui";

export function CellReceipts({ installmentIds, onChanged }: { installmentIds: string[]; onChanged: () => void }) {
  const { user, can } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [rows, setRows] = useState<any[] | null>(null);
  const [target, setTarget] = useState<any>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/payments/by-installments", { params: { ids: installmentIds.join(",") } }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { setRows(null); if (installmentIds.length) load(); }, [installmentIds.join(",")]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isSuper) {
        await api.delete(`/payments/${target.id}`, { params: { reason } });
        toast.success(`${target.receipt_no} di-void, alokasi cicilan dikembalikan`);
      } else {
        await api.post(`/payments/${target.id}/void-request`, { reason });
        toast.success("Pengajuan void dikirim ke Approval");
      }
      setTarget(null); setReason("");
      load(); onChanged();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  if (!installmentIds.length) return null;
  return (
    <div className="space-y-1.5" data-testid="cell-receipts">
      <p className="text-xs font-semibold text-muted-foreground">Kwitansi yang membayar cicilan ini</p>
      {rows === null ? <p className="text-xs text-muted-foreground">Memuat…</p> : rows.length === 0 ? <p className="text-xs text-muted-foreground" data-testid="cell-receipts-empty">Belum ada pembayaran.</p> : (
        <table className="yt-table text-xs"><thead><tr><th>No. Kwitansi</th><th>Tanggal</th><th className="num">Nominal</th><th className="num">Ke cicilan ini</th><th></th></tr></thead><tbody>
          {rows.map((p) => (
            <tr key={p.id} data-testid={`cell-receipt-${p.receipt_no}`}>
              <td className="font-mono">{p.receipt_no}</td>
              <td>{fmtDate(p.date)}</td>
              <td className="num">{fmtIDR(p.amount)}</td>
              <td className="num">{fmtIDR(p.allocated_here)}</td>
              <td className="text-right">
                {p.void_pending ? <span className="text-[10px] text-amber-700 font-semibold">menunggu approval</span>
                  : can(WRITE_ROLES) && <button type="button" className="inline-flex items-center gap-1 text-red-600 font-semibold hover:underline" data-testid={`cell-void-${p.receipt_no}`} onClick={() => setTarget(p)}><Undo2 className="h-3 w-3" /> {isSuper ? "Void" : "Ajukan void"}</button>}
              </td>
            </tr>
          ))}
        </tbody></table>
      )}
      <Modal open={!!target} onClose={() => setTarget(null)} title={`${isSuper ? "Void" : "Ajukan Void"} · ${target?.receipt_no}`} testId="cell-void-modal">
        <form onSubmit={submit} className="space-y-3">
          <p className="text-sm text-muted-foreground">Kwitansi {fmtIDR(target?.amount)} tgl {fmtDate(target?.date)} tidak dihapus; {isSuper ? "langsung" : "setelah disetujui,"} alokasi ke cicilan dibalik, kas berkurang, dan kwitansi ditandai VOID. Tercatat di audit trail.</p>
          <Field label="Alasan void (wajib)"><Textarea data-testid="cell-void-reason" required value={reason} onChange={(e) => setReason(e.target.value)} placeholder="mis. salah input siswa / pembayaran dibatalkan" /></Field>
          <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setTarget(null)}>Batal</Button><Button type="submit" variant="danger" disabled={busy} data-testid="cell-void-submit">{busy ? "Memproses…" : isSuper ? "Void Sekarang" : "Ajukan Void"}</Button></div>
        </form>
      </Modal>
    </div>
  );
}
